/**
 * AuthProvider — provider del estado global de autenticación.
 *
 * Definición del contexto puro vive en `auth-context.ts` (separado para que
 * react-refresh funcione mejor: este archivo solo exporta componentes).
 *
 * Responsabilidades:
 * - Mantener `user` y `status` en memoria.
 * - Hidratar desde localStorage al montar (si hay refresh token, llama /auth/me).
 * - Exponer acciones: login, register, logout.
 * - Escuchar el evento global AUTH_LOGOUT_EVENT que dispara client.ts cuando
 *   el refresh falla, y limpiar estado local.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import * as authApi from "@/api/auth";
import { AUTH_LOGOUT_EVENT, tokenStorage } from "@/api/client";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
} from "@/contexts/auth-context";
import type { User } from "@/types/api";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  // Evita doble hidratación en StrictMode (development renderiza 2x).
  const hydratedOnce = useRef(false);

  // ---- Hidratación inicial ----
  // BUG-002 fix: el flag `cancelled` combinado con StrictMode + cleanup
  // dejaba `status` en "loading" infinito tras F5 en ruta protegida —
  // el cleanup del primer mount de StrictMode marcaba cancelled=true
  // ANTES de que /auth/me resolviera, así el setState nunca corría.
  // `hydratedOnce.current` ya garantiza que el effect solo se ejecuta
  // una vez; React 18+ silencia el warning de setState-on-unmounted, así
  // que el flag era redundante y aquí ACTIVAMENTE dañino.
  useEffect(() => {
    if (hydratedOnce.current) return;
    hydratedOnce.current = true;

    const refresh = tokenStorage.getRefresh();
    if (!refresh) {
      setStatus("anonymous");
      return;
    }

    (async () => {
      try {
        const { user: u } = await authApi.me();
        setUser(u);
        setStatus("authenticated");
      } catch {
        tokenStorage.clear();
        setUser(null);
        setStatus("anonymous");
      }
    })();
  }, []);

  // ---- Listener del evento global de logout ----
  useEffect(() => {
    function handle() {
      setUser(null);
      setStatus("anonymous");
    }
    window.addEventListener(AUTH_LOGOUT_EVENT, handle);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handle);
  }, []);

  // ---- Acciones ----
  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login({ email, password });
    setUser(data.user);
    setStatus("authenticated");
  }, []);

  const register = useCallback(
    async (
      email: string,
      password: string,
      inviteCode: string,
      fullName: string | undefined,
      acceptTerms: boolean,
    ) => {
      const data = await authApi.register({
        email,
        password,
        invite_code: inviteCode,
        accept_terms: acceptTerms,
        ...(fullName ? { full_name: fullName } : {}),
      });
      setUser(data.user);
      setStatus("authenticated");
    },
    [],
  );

  const googleSignIn = useCallback(
    async (idToken: string, inviteCode?: string, acceptTerms?: boolean) => {
      const data = await authApi.googleSignIn(idToken, inviteCode, acceptTerms);
      setUser(data.user);
      setStatus("authenticated");
    },
    [],
  );

  const appleSignIn = useCallback(
    async (
      idToken: string,
      name?: { firstName: string; lastName: string },
      acceptTerms?: boolean,
    ) => {
      const data = await authApi.appleSignIn(idToken, name, acceptTerms);
      setUser(data.user);
      setStatus("authenticated");
    },
    [],
  );

  const logout = useCallback(async () => {
    // Logout NUNCA propaga errores: si el server falla, el frontend igual
    // limpia su estado y el usuario percibe sesión cerrada. El refresh viejo
    // expirará solo (máx 30 días) y el server lo aceptará como inactivo.
    try {
      await authApi.logout();
    } catch {
      // intencional: silencioso
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const deleteAccount = useCallback(
    async (args: { password?: string; confirm?: boolean }) => {
      // A diferencia de logout, SÍ propagamos el error (invalid_password /
      // confirmation_required) para que la UI lo muestre y NO cierre sesión.
      await authApi.deleteAccount(args);
      // Éxito (204): la cuenta ya no existe → estado anónimo.
      setUser(null);
      setStatus("anonymous");
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      login,
      register,
      googleSignIn,
      appleSignIn,
      logout,
      deleteAccount,
    }),
    [
      user,
      status,
      login,
      register,
      googleSignIn,
      appleSignIn,
      logout,
      deleteAccount,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
